<template>
  <div>
    <div class="p-box">
      <Loading v-if="gmbStoresLoading" />
      <template v-else>
        <Loading v-if="loading" />
        <template v-else>
          <div v-if="!mediaLibrariesExists" class="p-box__body noMediaBox">
            <p>{{ $t('pages.CompaniesMediaLibraries.noMedia.notice') }}</p>
            <p v-if="hasGmbStore || hasYahooPlaceStore" class="fontBold">
              {{ $t('pages.CompaniesMediaLibraries.noMedia.canUseServices', { services: canUseServiceNames }) }}
            </p>
            <div class="c-button c-button--left">
              <router-link :to="{ name: 'CompaniesMediaLibraryUpload', params: { company_uuid: companyUuid } }" class="c-button__item c-button__item--primary">
                {{ $t('pages.CompaniesMediaLibraries.noMedia.btnText') }}
              </router-link>
            </div>
          </div>
          <div v-else>
            <header class="p-box__header">
              <div class="p-filtersA">
                <div class="p-filtersA__left">
                  <div class="c-buttonFilter" @click="openModal">
                    {{ $t('labels.filters') }}
                    <div class="c-buttonFilter__icon">
                      <Icon name="iconFilter" />
                    </div>
                  </div>
                  <div v-show="true" class="c-select">
                    <select value="" :disabled="mediaLibraryIds.length === 0" @change="handleSelectAction($event.target.value)">
                      <option value="" disabled>{{ $t('pages.CompaniesLocationStores.bulkUpdate') }}</option>
                      <option value="bulkSet" :disabled="!hasGmbStore && !hasYahooPlaceStore">
                        {{ $t('pages.CompaniesMediaLibraries.multiSelectOptions.set') }}
                      </option>
                      <option v-if="showOnlyOwnerAuthority" value="bulkDelete">
                        {{ $t('pages.CompaniesMediaLibraries.multiSelectOptions.delete') }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="p-filtersA__right">
                  <div class="sortForm c-buttonSort">
                    <button
                      class="c-buttonSort__item"
                      :class="`is-${sortOrder}`"
                      type="button"
                      :disabled="mediasLoading"
                      @click="sort()"
                      v-text="$t('labels.sort.date')"
                    />
                  </div>
                  <div class="addPhoto">
                    <KkButton color="primary" full :text="$t('pages.CompaniesMediaLibraries.addPhoto')" :to="{ name: 'CompaniesMediaLibraryUpload', params: { company_uuid: companyUuid } }" />
                  </div>
                </div>
              </div>
            </header>
            <div class="p-box__body">
              <Loading v-show="mediasLoading" />
              <template v-if="!mediasLoading">
                <div v-if="medias" class="p-photos">
                  <div class="p-photos__total_count">
                    {{ $t('pages.CompaniesMediaLibrary.totalCount', { number: numberLocaleString(mediasMeta.totalCount) }) }}
                  </div>
                  <div class="p-photos__items">
                    <div v-for="media in medias"
                         :key="'media_' + media.id"
                         class="p-photos__item"
                    >
                      <div>
                        <div
                          class="p-photos__image"
                          :class="{ 'p-photos__image--processing': media.status === 'processing' }"
                          @click="handlePhotoClick(media.id)"
                        >
                          <div :style="{ 'background-image': `url(${ media.mediaImageUrl })` }" />
                        </div>
                        <div class="p-photos__name">
                          <TextMiddleEllipsis :text="media.fileName" :end-offset="7" :max-row="2" />
                        </div>
                        <div class="p-photos__summary">
                          <p class="statusMsg">
                            <span v-if="media.status !== 'notSet'" :class="`statusMark__${media.status}`" class="statusMark" />
                            {{ statusMsg(media) }}
                          </p>
                          <p>
                            {{ $t('pages.CompaniesMediaLibraries.storeCount') }}
                            <UnUsedBadge v-if="extractStoreCount(media.storeCount) === 0" />
                            <span v-else class="fontBold">
                              {{ $t('labels.simpleStoresCount', { count: extractStoreCount(media.storeCount) }) }}
                            </span>
                          </p>
                          <RegisteredProduction v-if="media.productRegistered" />
                          <p v-if="media.status !== 'processing'">
                            <input :id="'photo_' + media.id"
                                   type="checkbox"
                                   class="c-checkbox__input"
                                   @change="handleChangeMediaLibraryIds(media.id)"
                            >
                            <label
                              class="c-checkbox__label"
                              :for="'photo_' + media.id"
                            >
                              {{ $t('pages.CompaniesMediaLibraries.select') }}
                            </label>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else v-text="$t('messages.noData')" />
                <div v-if="mediasMeta.totalPages" class="p-controller">
                  <div class="p-controller__count">
                    <p class="p-controller__count__name">
                      {{ $t('labels.perPageCount') }}
                    </p>
                    <div class="p-controller__count__select">
                      <select :value="displayNumber" @change="changeDisplayNumber($event.target.value)">
                        <option v-for="item in 5" :key="item" :value="item * 50">{{ item * 50 }}</option>
                      </select>
                    </div>
                    <p class="p-controller__count__current">{{ startDataNumber }}-{{ endDataNumber }}</p>
                  </div>
                  <div class="p-controller__navi">
                    <button
                      type="button"
                      :disabled="mediasLoading || currentPage === 1"
                      class="p-controller__navi__prev"
                      @click="goPrevPage"
                    >
                      <Icon name="arrowLeft" />
                    </button>
                    <button
                      type="button"
                      :disabled="mediasLoading || currentPage >= mediasMeta.totalPages"
                      class="p-controller__navi__next"
                      @click="goNextPage"
                    >
                      <Icon name="arrowRight" />
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </template>
        <KkFilterModal
          :is-visible="modal"
          :filter-items="filterItems"
          :form-values="formValues"
          :total-count="filteredCount"
          @close="modal = false"
          @submit="handleResetPageAndSubmit"
          @reset="reset"
          @change-check-box="handleChangeFilterModalCheckBox"
        />
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import Loading from '../../components/Loading'
import Icon from '../../components/Icon'
import { toNumberLocaleString } from '../../modules/localeString'
import { KkFilterModal } from '../../components/projects'
import UnUsedBadge from '../../components/CompaniesMediaLibraries/UnUsedBadge'
import RegisteredProduction from '../../components/CompaniesMediaLibraries/RegisteredProduction'
import TextMiddleEllipsis from '../../components/TextMiddleEllipsis'
import { addAttributes } from '../../modules/filterModalConst'
import { KkButton } from '../../components/base'

export default {
  name: 'CompaniesMediaLibraries',
  components: {
    UnUsedBadge,
    RegisteredProduction,
    Loading,
    Icon,
    KkFilterModal,
    TextMiddleEllipsis,
    KkButton
  },
  data() {
    return {
      currentPage: 1,
      modal: false,
      sortOrder: 'desc',
      sortKey: 'updated_at',
      datePickerOpened: false,
      filterItems: []
    }
  },
  computed: {
    ...mapState({
      medias: state => state.companiesMediaLibraries.data,
      mediaLibraryIds: state => state.companiesMediaLibraries.mediaLibraryIds,
      mediasMeta: state => state.companiesMediaLibraries.meta,
      mediasLoading: state => state.companiesMediaLibraries.loading,
      filteredCount: state => state.companiesMediaLibraries.filteredCount,
      gmbStoresLoading: state => state.companiesGmbStores.loading,
      displayNumber: state => state.filters.displayNumber,
      searchAttributes: state => state.companiesSearchTagGroups.data,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
      formValues: state => state.companiesMediaLibraries.formValues,
    }),
    ...mapGetters({
      showOnlyOwnerAuthority: 'companiesAccessibleApi/showOnlyOwnerAuthority',
      hasGmbStore: 'companiesCompany/hasGmbStore',
      hasYahooPlaceStore: 'companiesCompany/hasYahooPlaceStore',
    }),
    mediaLibrariesExists() {
      return this.mediasMeta?.mediaLibrariesExists
    },
    companyUuid() { return this.$route.params.company_uuid },
    startDataNumber() {
      return (this.currentPage - 1) * this.displayNumber + 1
    },
    endDataNumber() {
      return this.startDataNumber + this.medias.length - 1
    },
    query() {
      let query = {
        page: this.currentPage,
        per: Number(this.displayNumber),
        sort_key: this.sortKey,
        sort_order: this.sortOrder,
      }
      query.stores_filter = {
        store_uuids: this.formValues.stores,
        brand_ids: this.formValues.brands,
        franchisee_ids: this.formValues.franchisees,
        user_company_ids: this.formValues.user_companies,
        area_ids: this.formValues.areas,
        prefectures: this.formValues.prefectures,
        original_tag_group_a_tag_ids: this.formValues.original_tag_group_a,
        original_tag_group_b_tag_ids: this.formValues.original_tag_group_b,
        original_tag_group_c_tag_ids: this.formValues.original_tag_group_c,
        original_tag_group_d_tag_ids: this.formValues.original_tag_group_d,
        original_tag_group_e_tag_ids: this.formValues.original_tag_group_e,
      }
      return query
    },
    loading() {
      return this.mediasLoading ||  this.gmbStoresLoading || this.searchAttributesLoading
    },
    canUseServiceNames() {
      const services = []
      if (this.hasGmbStore) services.push(this.$t('labels.sites.google'))
      if (this.hasYahooPlaceStore) services.push(this.$t('labels.sites.yahooMap'))
      return services.join(', ')
    },
  },
  async created() {
    this.initMediaLibrariesState()
    await Promise.all([
      this.readGmbStores(this.$route.params.company_uuid),
      this.readSearchAttributes({ inclusion_unset_store: true }),
    ])
    this.setFormValues(this.formInitialValues())
    this.createFilter()
    this.readCompaniesMediaLibraries(this.query)
  },
  methods: {
    ...mapActions({
      readSearchAttributes: 'companiesSearchTagGroups/readSearchTagGroups',
      readGmbStores: 'companiesGmbStores/readGmbStores',
      readCompaniesMediaLibraries: 'companiesMediaLibraries/readCompaniesMediaLibraries',
      readCompaniesMediaLibrariesForFilter: 'companiesMediaLibraries/readCompaniesMediaLibrariesForFilter',
      setDisplayNumber: 'filters/setDisplayNumber',
      setPeriod: 'filters/setPeriod',
      setMediaLibraryId: 'companiesMediaLibraries/setMediaLibraryId',
      initMediaLibrariesState: 'companiesMediaLibraries/initMediaLibrariesState',
      bulkDelete: 'companiesMediaLibraries/bulkDelete',
    }),
    ...mapMutations({
      setFormValues: 'companiesMediaLibraries/setFormValues'
    }),
    handlePhotoClick(id) {
      this.$router.push({ name: 'CompaniesMediaLibrary', params: { company_uuid: this.companyUuid, id: id } })
    },
    extractStoreCount(storeCount) {
      return storeCount.progress + storeCount.error + storeCount.complete
    },
    async handleSelectAction(val) {
      switch (val) {
      case 'bulkSet':
        this.$router.push({
          name: 'CompaniesMediaLibrariesBulkSet',
          params: { company_uuid: this.companyUuid }
        })
        return
      case 'bulkDelete':
        if (confirm(this.$t('pages.CompaniesMediaLibraries.bulkDelete'))) {
          if (await this.bulkDelete()) this.readCompaniesMediaLibraries(this.query)
        }
        return
      }
    },
    handleChangeMediaLibraryIds(id) {
      this.setMediaLibraryId(id)
    },
    formInitialValues() {
      return {
        // どの店舗にも紐づいていないものも出すため、ここは空
        stores: [],
        brands: [],
        franchisees: [],
        user_companies: [],
        areas: [],
        prefectures: [],
        original_tag_group_a: [],
        original_tag_group_b: [],
        original_tag_group_c: [],
        original_tag_group_d: [],
        original_tag_group_e: [],
      }
    },
    openModal() { this.modal = true },
    statusMsg(media) {
      if (media.status === 'error') {
        return this.$t('pages.CompaniesMediaLibraries.status.' + media.status, { count: media.storeCount.error })
      }
      return this.$t('pages.CompaniesMediaLibraries.status.' + media.status)
    },
    sort() {
      this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      this.handleScrollTopAndSubmit()
    },
    changeDisplayNumber(value) {
      this.setDisplayNumber(value)
      this.handleScrollTopAndSubmit()
    },
    goPrevPage() {
      --this.currentPage
      this.handleScrollTopAndSubmit()
    },
    goNextPage() {
      ++this.currentPage
      this.handleScrollTopAndSubmit()
    },
    handleScrollTopAndSubmit() {
      window.scrollTo({ top: 0 })
      this.handleSubmit()
    },
    reset() {
      this.setFormValues(this.formInitialValues())
      this.handleSubmit()
    },
    handleResetPageAndSubmit() {
      this.currentPage = 1
      this.handleSubmit()
    },
    handleSubmit() {
      this.readCompaniesMediaLibraries(this.query)
      this.datePickerOpened = false
      this.modal = false
    },
    numberLocaleString(val) {
      return toNumberLocaleString(val)
    },
    handleChangeFilterModalCheckBox({kind, id}) {
      const newFormValues = Object.assign({}, this.formValues)

      if(newFormValues[kind].includes(id)) {
        newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
      } else {
        newFormValues[kind].push(id)
      }

      this.setFormValues(newFormValues)
      this.readCompaniesMediaLibrariesForFilter(this.query)
    },
    createFilter() {
      const attributes = [
        this.searchAttributes.brands,
        this.searchAttributes.areas,
        this.searchAttributes.franchisees,
        this.searchAttributes.user_companies,
      ]

      if(this.searchAttributes.original_tag_group_a) attributes.push(this.searchAttributes.original_tag_group_a)
      if(this.searchAttributes.original_tag_group_b) attributes.push(this.searchAttributes.original_tag_group_b)
      if(this.searchAttributes.original_tag_group_c) attributes.push(this.searchAttributes.original_tag_group_c)
      if(this.searchAttributes.original_tag_group_d) attributes.push(this.searchAttributes.original_tag_group_d)
      if(this.searchAttributes.original_tag_group_e) attributes.push(this.searchAttributes.original_tag_group_e)

      attributes.push(this.searchAttributes.prefectures)
      attributes.push(this.searchAttributes.stores)

      this.filterItems = attributes.map((attr) => {
        if(attr && attr.kind){
          return addAttributes(attr, attr.kind)
        }
      })
    }
  },
}
</script>

<style lang="scss" scoped>
@import "../../../../stylesheets/biz_spa/foundation/_var.scss";
.p-filtersA__right {
  align-items: flex-start;
}
.noMediaBox p {
  margin-bottom: 16px;
}
.fontBold {
  font-weight: bold;
}
.statusMsg__error {
  color: $color-accent-primary;
}
.statusMark {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  &__error {
    background: $color-accent-primary;
  }
  &__completed {
    background: $complete-color;
  }
  &__processing {
    background: #D2D5DB;
  }
}
.p-photos__summary {
  padding-bottom: 0;
  p {
    margin: 8px 0;
  }
}
.p-photos__image {
  cursor: pointer;
  &--processing {
    opacity: .7;
  }
}
.p-photos__name {
  margin-top: 10px;
}
.c-buttonFilter__icon--red {
  color: red;
}
.addPhoto {
  margin-left: 8px;
  @media screen and (max-width: 768px) {
    margin-top: 16px;
    margin-left: 0;
  }
}
</style>
